import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
} from "@material-ui/core";

import useStyles from "./styles";
import { NoDataFound } from "../NoDataFound";

function CustomExpandableTable({
  data = [],
  headerData = [],
  rowHeight = 84,
  tableActions,
  onRowClick,
  onRowDoubleClick,
  expandRowContent,
  widthHighlight = false,
  loading = false,
}) {
  const [openRows, setOpenRows] = useState({});
  const { isDarkMode } = useSelector((state) => state.theme);
  const styles = useStyles({ isDarkMode });

  const [activeRow, setActiveRow] = useState(null);
  const headers = useMemo(
    () =>
      headerData.length > 0
        ? headerData.map((header) => header.headerName)
        : [],
    [headerData]
  );
  const containerRef = useRef(null);

  // Mouse drag-to-scroll logic (handlers memoized for performance)
  const mouseDownHandler = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;
    container.isDown = true;
    container.classList.add("dragging");
    container.startX = e.pageX - container.offsetLeft;
    container.scrollLeftStart = container.scrollLeft;
  }, []);

  const mouseLeaveHandler = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    container.isDown = false;
    container.classList.remove("dragging");
  }, []);

  const mouseUpHandler = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    container.isDown = false;
    container.classList.remove("dragging");
  }, []);

  const mouseMoveHandler = useCallback((e) => {
    const container = containerRef.current;
    if (!container || !container.isDown) return;
    e.preventDefault();
    const x = e.pageX - container.offsetLeft;
    const walk = x - container.startX;
    container.scrollLeft = container.scrollLeftStart - walk;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("mousedown", mouseDownHandler);
    container.addEventListener("mouseleave", mouseLeaveHandler);
    container.addEventListener("mouseup", mouseUpHandler);
    container.addEventListener("mousemove", mouseMoveHandler);
    return () => {
      container.removeEventListener("mousedown", mouseDownHandler);
      container.removeEventListener("mouseleave", mouseLeaveHandler);
      container.removeEventListener("mouseup", mouseUpHandler);
      container.removeEventListener("mousemove", mouseMoveHandler);
    };
  }, [mouseDownHandler, mouseLeaveHandler, mouseUpHandler, mouseMoveHandler]);

  // Memoize row click handlers for stable references
  const handleRowClick = useCallback(
    (_, row) => {
      if (activeRow === row.id) {
        return;
      } else {
        setActiveRow(row.id);
        if (onRowClick) onRowClick(row);
      }
    },
    [onRowClick, activeRow]
  );

  const handleRowDoubleClick = useCallback(
    (row) => {
      if (onRowDoubleClick) onRowDoubleClick(row);
    },
    [onRowDoubleClick]
  );

  const handleScroll = (e) => {
    setActiveRow(null);
  };

  // Memoize rows for performance
  const memoizedRows = useMemo(() => {
    return data.map((row) => (
      <React.Fragment key={row.id}>
        <TableRow
          hover
          selected={widthHighlight && activeRow === row.id}
          onClick={(e) => handleRowClick(e, row)}
          onDoubleClick={() => handleRowDoubleClick(row)}
          style={{ cursor: "pointer", height: rowHeight, position: "relative" }}
        >
          <TableCell />
          {headers.map((header, index) => (
            <TableCell key={header} className={styles.tableCell}>
              {headerData[index].renderCell
                ? headerData[index].renderCell({
                    row: {
                      [headerData[index].field]: row[headerData[index].field],
                    },
                  })
                : row[headerData[index].field]}
            </TableCell>
          ))}

          {tableActions && activeRow === row.id && (
            <TableCell
              style={{
                position: "sticky",
                right: 0,
                zIndex: 2,
                background: "#fff",
                border: "none",
                minWidth: 80,
                maxWidth: 160,
                padding: 0,
                textAlign: "right",
              }}
            >
              {tableActions({
                row,
                onExpand: () =>
                  setOpenRows((prev) => ({ ...prev, [row.id]: !prev[row.id] })),
              })}
            </TableCell>
          )}
        </TableRow>
        <TableRow>
          <TableCell
            style={{ padding: 0 }}
            colSpan={headers.length + 1 + (tableActions ? 1 : 0)}
          >
            <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
              <Table size="small" className={styles.detailTable}>
                <TableRow>
                  <TableCell className={styles.detailTableCell}>
                    {expandRowContent(row)}
                  </TableCell>
                </TableRow>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    ));
  }, [
    data,
    activeRow,
    openRows,
    rowHeight,
    headers,
    headerData,
    styles,
    handleRowClick,
    handleRowDoubleClick,
    tableActions,
  ]);

  return (
    <TableContainer
      component={Paper}
      ref={containerRef}
      className={styles.tableContainer}
      onScroll={handleScroll}
      style={{ position: "relative", width: "100%" }}
    >
      <Table stickyHeader aria-label="expandable table">
        <TableHead>
          <TableRow>
            <TableCell className={styles.firstCell} />
            {headers.map((header) => (
              <TableCell key={header} className={styles.tableHeadCell}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody >
          {data.length > 0 && !loading ? (
            memoizedRows
          ) : (
            <NoDataFound wrapperClass={styles.noDataWrapper} title="No data found" subTitle="" />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CustomExpandableTable;
